import { useEffect, useMemo, useState } from 'react';
import type { SubTable } from '../../domain';
import type { TableAdapter } from '../../ports';

export interface SubTableState {
    childAdapter: TableAdapter<unknown> | null;
    childSelectedRows: unknown[];
    setChildSelectedRows: (rows: unknown[]) => void;
}

export interface UseSubTableArgs<T> {
    // eslint-disable-next-line typescript/no-explicit-any -- see TableProps.subTable for why `any`, not `unknown`
    subTable?: SubTable<T, any>;
    expandedId: string | null;
    rows: T[];
    getRowId: (row: T) => string;
    // eslint-disable-next-line typescript/no-explicit-any -- data is the return value of subTable.data(row)
    toAdapter: (data: any) => TableAdapter<unknown>;
}

export function useSubTable<T>(args: UseSubTableArgs<T>): SubTableState {
    const { subTable, expandedId, rows, getRowId, toAdapter } = args;

    const [childSelectedRows, setChildSelectedRows] = useState<unknown[]>([]);
    useEffect(() => {
        setChildSelectedRows([]);
        subTable?.onSelectionChange?.([], []);
        // eslint-disable-next-line react/exhaustive-deps -- reset belongs to a change of expanded row, not to identity changes of the sub-table config
    }, [expandedId]);

    const expandedRow = useMemo(() => {
        if (expandedId === null) return null;
        return rows.find((r) => getRowId(r) === expandedId) ?? null;
    }, [rows, expandedId, getRowId]);
    const data = subTable?.data;

    const childAdapter = useMemo<TableAdapter<unknown> | null>(() => {
        if (!data || !expandedRow) return null;
        return toAdapter(data(expandedRow));
        // eslint-disable-next-line react/exhaustive-deps -- an adapter is stateful and must be rebuilt only when its data source or expanded row changes. Sub-table columns, actions, and selection callbacks can update without resetting child selection, sort, filters, or pagination. `toAdapter` is an inline factory in Table — its identity must not trigger a rebuild either.
    }, [data, expandedRow]);

    return { childAdapter, childSelectedRows, setChildSelectedRows };
}
